import React from "react";
import { Helmet } from "react-helmet-async";
import { SITE } from "@/lib/site";

export default function SEO({
  title,
  description,
  canonical,
  ogImage,
  jsonLd,
  noIndex = false,
}) {
  const fullTitle = title ? `${title}` : SITE.defaultTitle;
  const desc = description || SITE.description;
  // Canonical URL always points to the production domain. Build the path from
  // the current pathname (when available) so each route's canonical is unique.
  const path = typeof window !== "undefined" ? window.location.pathname + window.location.search : "";
  const url = canonical || `${SITE.canonicalDomain}${path}`;
  const image = ogImage || "https://images.unsplash.com/photo-1503594384566-461fe158e797?w=1600&q=80";

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {noIndex && <meta name="robots" content="noindex,nofollow" />}
      <link rel="canonical" href={url} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={SITE.name} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={image} />
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}

export function buildLocalBusinessJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": ["GeneralContractor", "RoofingContractor", "LocalBusiness"],
    name: SITE.name,
    description: SITE.description,
    telephone: SITE.phone,
    email: SITE.email,
    url: SITE.domain,
    areaServed: [
      { "@type": "City", name: "Austin" },
      { "@type": "City", name: "Round Rock" },
      { "@type": "City", name: "Cedar Park" },
      { "@type": "City", name: "Georgetown" },
      { "@type": "City", name: "Leander" },
      { "@type": "City", name: "Pflugerville" },
      { "@type": "City", name: "Kyle" },
      { "@type": "City", name: "Buda" },
      { "@type": "City", name: "San Marcos" },
      { "@type": "City", name: "Dripping Springs" },
    ],
    address: { "@type": "PostalAddress", addressLocality: "Austin", addressRegion: "TX", addressCountry: "US" },
  };
}

export function buildFaqJsonLd(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function buildBreadcrumbJsonLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: it.name,
      item: it.url,
    })),
  };
}
